import { CUSTOM_ELEMENTS_SCHEMA, inject, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LayoutsModule } from './dashboard/layouts/layouts.module';
import { AppComponentModule } from './core/services/appComponent.module';
import { ScrollToModule } from '@nicky-lenaers/ngx-scroll-to';
import { HTTP_INTERCEPTORS, HttpClient, HttpInterceptorFn, provideHttpClient, withInterceptors } from '@angular/common/http';
import { TranslateModule, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { ToastrModule } from 'ngx-toastr';
import { CookieService } from 'ngx-cookie-service';

import { AuthService } from './core/authservice/auth.service';
import { AuthGuard } from './core/authservice/guards/auth.guard';
import { GuestGuard } from './core/authservice/guards/guest.guard';
import { NetworkInterceptor } from './core/services/network-iterceptor';
import { TokenInterceptor } from './core/authservice/token-interceptor';
import { ErrorInterceptor } from './core/helpers/error.interceptor';

import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { menuReducer } from './dashboard/layouts/menu/menu.reducer';
import { MenuEffects } from './dashboard/layouts/menu/menu.effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { LoadingService } from './core/services/loading/loading.service';
import { finalize } from 'rxjs';
import { SplashScreenModule } from './splash-screen.module';
import { GeneralErrorComponent } from './extraspages/errors/general-error/general-error.component';
import { StudentLayoutModule } from './student/layouts/student-layout.module';

export const tokenInterceptorFn: HttpInterceptorFn = (req, next) => {
  const tokenInterceptor = inject(TokenInterceptor); // Inject TokenInterceptor
  return tokenInterceptor.intercept(req, {
    handle: (request) => next(request)
  }); // Delegate logic to TokenInterceptor
};
export const loadingInterceptorFn: HttpInterceptorFn = (req, next) => {
  const loadingService = inject(LoadingService);

  loadingService._setLoadingStatus(true, req.url);

  return next(req).pipe(
    finalize(() => {
      loadingService._setLoadingStatus(false, req.url);
    })
  );
};

// Translate Loader Factory
export function createTranslateLoader(http: HttpClient): TranslateLoader {
  return new TranslateHttpLoader(http, 'assets/i18n/', '.json');
}

// App Initializer
export function initializeApp(translateService: TranslateService): Promise<void> {
  return new Promise((resolve) => {
    const defaultLang = 'ar';
    translateService.setDefaultLang(defaultLang);
    translateService.use(defaultLang).subscribe(() => resolve());
  });
}


@NgModule({
  declarations: [
    AppComponent,
    GeneralErrorComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    SplashScreenModule,
    LayoutsModule,
    StudentLayoutModule,
    AppComponentModule,
    ScrollToModule.forRoot(),
    StoreModule.forRoot({
      menu: menuReducer
    }), // if you have a root
    StoreDevtoolsModule.instrument({
      maxAge: 25 // # of previous states to store for debugging
    }),
    EffectsModule.forRoot([MenuEffects]),
    TranslateModule.forRoot({
      defaultLanguage: 'ar',
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient]
      }
    }),
    ToastrModule.forRoot({
      timeOut: 5000,
      positionClass: 'toast-bottom-right',
      preventDuplicates: true,
      tapToDismiss: true,
      progressBar: true
    }),
  ],
  providers: [
    CookieService,
    AuthService,
    AuthGuard,
    GuestGuard,
    provideHttpClient(
      withInterceptors([
        loadingInterceptorFn,
        tokenInterceptorFn
      ])
    ),
    { provide: HTTP_INTERCEPTORS, useClass: NetworkInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    //provideAppInitializer(() => initializeApp(inject(TranslateService))),
    TokenInterceptor, // Ensure TokenInterceptor is provided
    AuthService, // Ensure AuthService is available
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule {}
